import { useSelector, useDispatch } from 'react-redux';
import {
  useGetUserBackgroundsQuery,
  useGetPublicBackgroundsQuery,
  useUploadBackgroundMutation,
  useUpdateBackgroundMutation,
  useDeleteBackgroundMutation
} from '../redux/apis/endpoints/backgroundImageApi';
import {
  setBackground,
  setBackgroundColor,
  setBackgroundGradient,
  setBackgroundImage,
  setBackgroundPreset,
  clearBackground,
  setLoading,
  setError,
  clearError
} from '../redux/slices/backgroundSlice';

/**
 * Custom hook for managing background images with backend persistence
 * Provides unified interface for background operations with RTK Query
 */
export const useBackgrounds = () => {
  const dispatch = useDispatch();
  
  // Get background state from Redux
  const background = useSelector(state => state.background.background);
  const showBackgroundCustomizer = useSelector(state => state.background.showBackgroundCustomizer);
  const history = useSelector(state => state.background.history);
  const settings = useSelector(state => state.background.settings);
  const isLoading = useSelector(state => state.background.isLoading);
  const uploadProgress = useSelector(state => state.background.uploadProgress);
  const error = useSelector(state => state.background.error);
  
  // Check authentication before making API calls
  const user = localStorage.getItem('user');
  const token = localStorage.getItem('token');
  const isAuthenticated = !!(user && token);

  // RTK Query hooks
  const { data: userBackgroundsData, isLoading: isLoadingUser, error: userError } = useGetUserBackgroundsQuery({
    active: true,
    sort: 'created_at:desc'
  }, {
    skip: !isAuthenticated // Skip query if not authenticated
  });
  
  const { data: publicBackgroundsData, isLoading: isLoadingPublic, error: publicError } = useGetPublicBackgroundsQuery({
    sort: 'usageCount:desc'
  }, {
    skip: !isAuthenticated // Skip query if not authenticated
  });
  
  // RTK Query mutations
  const [uploadBackground, { isLoading: isUploading }] = useUploadBackgroundMutation();
  const [updateBackground, { isLoading: isUpdating }] = useUpdateBackgroundMutation();
  const [deleteBackground, { isLoading: isDeleting }] = useDeleteBackgroundMutation();
  
  // Derived data
  const userBackgrounds = userBackgroundsData?.backgrounds || [];
  const publicBackgrounds = publicBackgroundsData?.backgrounds || [];
  const allBackgrounds = [...userBackgrounds, ...publicBackgrounds];
  
  // Background actions
  const handleSetBackground = (backgroundData) => {
    dispatch(setBackground({ background: backgroundData }));
  };
  
  const handleSetBackgroundColor = (color) => {
    dispatch(setBackgroundColor(color));
  };
  
  const handleSetBackgroundGradient = (gradient) => {
    dispatch(setBackgroundGradient(gradient));
  };
  
  const handleSetBackgroundImage = async (imageUrl, imageFile, backgroundId = null) => {
    dispatch(setBackgroundImage({ imageUrl, imageFile, backgroundId }));
    
    // If this is a backend image, update its usage count
    if (backgroundId) {
      try {
        await updateBackground({
          id: backgroundId,
          lastUsed: new Date().toISOString()
        }).unwrap();
      } catch (error) {
        console.error('Failed to update background usage:', error);
      }
    }
  };
  
  const handleSetBackgroundPreset = (preset) => {
    dispatch(setBackgroundPreset(preset));
  };
  
  const handleClearBackground = () => {
    dispatch(clearBackground());
  };
  
  // Upload background to backend
  const handleUploadBackground = async ({ file, name, description, category = 'custom', isPublic = false, tags }) => {
    try {
      dispatch(setLoading(true));
      
      const result = await uploadBackground({
        file,
        name: name || file.name,
        description,
        category,
        isPublic,
        tags
      }).unwrap();
      
      // Automatically set as current background
      if (result.image?.url) {
        handleSetBackgroundImage(result.image.url, null, result.id);
      }
      
      dispatch(setLoading(false));
      return result;
    } catch (error) {
      dispatch(setError(error.message || 'Upload failed'));
      throw error;
    }
  };
  
  // Delete background from backend
  const handleDeleteBackground = async (backgroundId) => {
    try {
      await deleteBackground(backgroundId).unwrap();
      
      // If the deleted background was the current one, clear it
      if (background.type === 'image' && background.imageUrl) {
        const deletedBg = allBackgrounds.find(bg => bg.id === backgroundId);
        if (deletedBg && deletedBg.image?.url === background.imageUrl) {
          handleClearBackground();
        }
      }
      
      return true;
    } catch (error) {
      dispatch(setError(error.message || 'Delete failed'));
      throw error;
    }
  };
  
  // Get background by ID
  const getBackgroundById = (backgroundId) => {
    return allBackgrounds.find(bg => bg.id === backgroundId);
  };
  
  // Check if a background is from backend
  const isBackendBackground = (imageUrl) => {
    return allBackgrounds.some(bg => bg.image?.url === imageUrl);
  };
  
  // Clear error
  const handleClearError = () => {
    dispatch(clearError());
  };
  
  return {
    // Current background state
    background,
    showBackgroundCustomizer,
    history,
    settings,
    
    // Background collections
    userBackgrounds,
    publicBackgrounds,
    allBackgrounds,
    
    // Loading states
    isLoading: isLoading || isLoadingUser || isLoadingPublic,
    isUploading,
    isUpdating,
    isDeleting,
    uploadProgress,
    
    // Error state
    error: error || userError || publicError,
    
    // Actions
    setBackground: handleSetBackground,
    setBackgroundColor: handleSetBackgroundColor,
    setBackgroundGradient: handleSetBackgroundGradient,
    setBackgroundImage: handleSetBackgroundImage,
    setBackgroundPreset: handleSetBackgroundPreset,
    clearBackground: handleClearBackground,
    
    // Backend operations
    uploadBackground: handleUploadBackground,
    deleteBackground: handleDeleteBackground,
    
    // Utilities
    getBackgroundById,
    isBackendBackground,
    clearError: handleClearError
  };
};

export default useBackgrounds;
